import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Shield, Loader2, AlertCircle, ChevronLeft, X, Send, MapPin, Navigation } from 'lucide-react'
import L from 'leaflet'
import { supabase } from '../lib/supabase'
import { useToast } from '../hooks/useToast'
import Toast from '../components/Toast'

// ── Leaflet map (no react-leaflet, avoids React 18 compat issues) ─────────────
function ScannerMap({ session, plate }) {
  const mapRef = useRef(null)
  const instanceRef = useRef(null)

  useEffect(() => {
    if (!mapRef.current || instanceRef.current) return
    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    })
    const map = L.map(mapRef.current, {
      center: [session.lat, session.lng],
      zoom: 17,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
    })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map)
    L.marker([session.lat, session.lng]).addTo(map).bindPopup(`${plate} parked here`)
    instanceRef.current = map
    return () => { map.remove(); instanceRef.current = null }
  }, [session.lat, session.lng, plate])

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,160,0.2)', background: '#13131a' }}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: 'rgba(0,229,160,0.07)' }}>
        <MapPin size={13} className="text-accent" />
        <span className="text-xs font-bold text-accent">Vehicle is parked here</span>
      </div>
      <div ref={mapRef} style={{ height: '160px', width: '100%' }} />
      <button
        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${session.lat},${session.lng}`, '_blank')}
        className="w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-all hover:opacity-90"
        style={{ background: 'rgba(0,229,160,0.1)', color: '#00e5a0' }}
      >
        <Navigation size={12} />
        Get Directions to Vehicle
      </button>
    </div>
  )
}

const VEHICLE_ICONS = { car: '🚗', bike: '🏍️', truck: '🚚', auto: '🛺', pet: '🐾', child: '👶' }
const VEHICLE_LABELS = { car: 'Car', bike: 'Bike', truck: 'Truck', auto: 'Auto-rickshaw', pet: 'Pet', child: 'Child' }
const PET_ANIMAL_ICONS = { dog: '🐕', cat: '🐈', bird: '🐦', rabbit: '🐇', other: '🐾' }

const QUICK_REASONS = [
  { icon: '🚧', label: 'Blocking my way' },
  { icon: '💡', label: 'Headlights left on' },
  { icon: '🔔', label: 'Car alarm going off' },
  { icon: '💥', label: 'Minor accident / scratch' },
  { icon: '🅿️', label: 'Wrong parking spot' },
  { icon: '🚨', label: 'Emergency — move now' },
]

const PET_QUICK_REASONS = [
  { icon: '🐾', label: 'Found your pet' },
  { icon: '📍', label: 'Your pet is near me' },
  { icon: '🏥', label: 'Your pet seems injured' },
  { icon: '🚪', label: 'Your pet is at my door' },
  { icon: '🧭', label: 'Share your location' },
  { icon: '🆘', label: 'Emergency — please call' },
]

const CHILD_QUICK_REASONS = [
  { icon: '👶', label: 'Found your child' },
  { icon: '📍', label: 'Your child is near me' },
  { icon: '🏥', label: 'Your child needs help' },
  { icon: '🏫', label: 'Found near a school' },
  { icon: '🧭', label: 'Share my location' },
  { icon: '🆘', label: 'Emergency — please call' },
]

// ── Rate limiting (localStorage, per tagCode per device) ──────────────────────
const MAX_CONTACTS_PER_DAY = 3
const COOLDOWN_MS = 5 * 60 * 1000     // 5 min between contacts
const WINDOW_MS   = 24 * 60 * 60 * 1000  // 24h rolling window

function getContactLog(code) {
  try {
    const raw = localStorage.getItem(`pp_rl_${code}`)
    if (!raw) return []
    return JSON.parse(raw).filter(t => Date.now() - t < WINDOW_MS)
  } catch { return [] }
}

function checkRateLimit(code) {
  const log = getContactLog(code)
  if (log.length >= MAX_CONTACTS_PER_DAY)
    return { allowed: false, reason: 'daily_limit', contactsLeft: 0 }
  const last = log[log.length - 1]
  if (last && Date.now() - last < COOLDOWN_MS)
    return { allowed: false, reason: 'cooldown', remainingMs: COOLDOWN_MS - (Date.now() - last) }
  return { allowed: true, contactsLeft: MAX_CONTACTS_PER_DAY - log.length }
}

function recordContact(code) {
  const log = getContactLog(code)
  log.push(Date.now())
  localStorage.setItem(`pp_rl_${code}`, JSON.stringify(log))
}

function fmtCooldown(secs) {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

export default function Scanner() {
  const { tagCode } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast, showToast, dismissToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tagData, setTagData] = useState(null)  // { tag, vehicle }
  const [contacting, setContacting] = useState(null) // 'call' | 'whatsapp' | 'sms'
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [smsModal, setSmsModal] = useState(false)
  const [whatsappModal, setWhatsappModal] = useState(false)
  const [smsMessage, setSmsMessage] = useState('')
  const [smsSending, setSmsSending] = useState(false)
  const [whatsappSending, setWhatsappSending] = useState(false)
  const [whatsappMessage, setWhatsappMessage] = useState('')
  const whatsappRef = useRef(null)
  const [callModal, setCallModal] = useState(false)
  const [callerPhone, setCallerPhone] = useState('')
  const [callInitiating, setCallInitiating] = useState(false)
  const textareaRef = useRef(null)
  const callInputRef = useRef(null)

  // Rate limit state
  const [rlStatus, setRlStatus]       = useState(null)
  const [cooldownSecs, setCooldownSecs] = useState(0)

  // Parking session (owner's active session for this tag)
  const [parkingSession, setParkingSession] = useState(null)

  // Keep rate limit status + countdown in sync (updates every second)
  useEffect(() => {
    if (!tagCode) return
    function tick() {
      const s = checkRateLimit(tagCode)
      setRlStatus(s)
      setCooldownSecs(s.reason === 'cooldown' ? Math.ceil(s.remainingMs / 1000) : 0)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [tagCode])

  useEffect(() => {
    // Demo mode — build fake tag data from URL params
    if (searchParams.get('demo') === '1') {
      const plate = searchParams.get('plate') || 'MH 04 AB 1234'
      const type = searchParams.get('type') || 'car'
      setTagData({
        tag: { id: 'demo', tag_code: tagCode, is_active: true },
        vehicle: { id: 'demo', plate, type, channel_call: true, channel_whatsapp: true, channel_sms: true, is_active: true },
      })
      setLoading(false)
      return
    }

    async function fetchTag() {
      if (!tagCode) { setError('Invalid tag link.'); setLoading(false); return }

      try {
        // Fetch tag
        const { data: tag, error: tagErr } = await supabase
          .from('tags')
          .select('*')
          .eq('tag_code', tagCode)
          .single()

        if (tagErr || !tag) {
          setError('Tag not found. The QR code may be invalid or removed.')
          setLoading(false)
          return
        }

        if (!tag.is_active) {
          setError('This tag is currently inactive. The vehicle owner has disabled it.')
          setLoading(false)
          return
        }

        // Fetch vehicle (don't expose owner phone — only channel settings)
        const { data: vehicle, error: vehErr } = await supabase
          .from('vehicles')
          .select('id, plate, type, channel_call, channel_whatsapp, channel_sms, is_active, pet_name, pet_animal, pet_breed, photo_url')
          .eq('id', tag.vehicle_id)
          .single()

        if (vehErr || !vehicle) {
          setError('Vehicle information not found.')
          setLoading(false)
          return
        }

        if (!vehicle.is_active) {
          setError('This tag has been deactivated by the owner.')
          setLoading(false)
          return
        }

        setTagData({ tag, vehicle })

        // Fetch active parking session for this tag
        const { data: ps } = await supabase
          .from('parking_sessions')
          .select('*')
          .eq('tag_code', tagCode)
          .eq('active', true)
          .maybeSingle()
        setParkingSession(ps || null)

      } catch (err) {
        setError('Something went wrong. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchTag()
  }, [tagCode, searchParams])

  function handleCallClick() {
    setCallModal(true)
    setCallerPhone('')
    setTimeout(() => callInputRef.current?.focus(), 100)
  }

  async function handleInitiateCall(e) {
    e.preventDefault()
    if (callerPhone.length < 10) return

    setCallInitiating(true)

    if (searchParams.get('demo') === '1') {
      await new Promise((r) => setTimeout(r, 800))
      setCallInitiating(false)
      setCallModal(false)
      recordContact(tagCode)
      showToast('📞 Calling your number now… (demo mode)', 'success')
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('initiate-call', {
        body: { tagCode, callerPhone: `+91${callerPhone}` },
      })
      if (error || !data?.ok) {
        showToast(data?.error || 'Failed to initiate call. Try again.', 'error')
      } else {
        recordContact(tagCode)
        setCallModal(false)
        showToast('📞 Calling your number now — answer to connect to owner', 'success')
      }
    } catch (err) {
      showToast('Network error. Please try again.', 'error')
    } finally {
      setCallInitiating(false)
    }
  }

  function handleWhatsAppClick() {
    setWhatsappModal(true)
    setWhatsappMessage(activeReason ? `Hi, regarding your vehicle ${tagData?.vehicle?.plate || ''} — ${activeReason}` : '')
    setTimeout(() => whatsappRef.current?.focus(), 100)
  }

  async function handleSendWhatsApp(e) {
    e.preventDefault()
    if (!whatsappMessage.trim()) return

    setWhatsappSending(true)

    if (searchParams.get('demo') === '1') {
      await new Promise((r) => setTimeout(r, 800))
      setWhatsappSending(false)
      setWhatsappModal(false)
      recordContact(tagCode)
      showToast('💬 WhatsApp sent to owner (demo mode)', 'success')
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: { tagCode, message: whatsappMessage.trim() },
      })
      if (error || !data?.ok) {
        showToast(data?.error || 'Failed to send WhatsApp. Try again.', 'error')
      } else {
        recordContact(tagCode)
        setWhatsappModal(false)
        showToast('💬 WhatsApp message delivered to vehicle owner', 'success')
      }
    } catch (err) {
      showToast('Network error. Please try again.', 'error')
    } finally {
      setWhatsappSending(false)
    }
  }

  function openSmsModal() {
    setSmsModal(true)
    setSmsMessage(activeReason ? `Hi, regarding your vehicle ${tagData?.vehicle?.plate || ''} — ${activeReason}` : '')
    setTimeout(() => textareaRef.current?.focus(), 100)
  }

  async function handleSendSms(e) {
    e.preventDefault()
    if (!smsMessage.trim()) return

    setSmsSending(true)

    // Demo mode
    if (searchParams.get('demo') === '1') {
      await new Promise((r) => setTimeout(r, 800))
      setSmsSending(false)
      setSmsModal(false)
      recordContact(tagCode)
      showToast('✉️ SMS sent to owner (demo mode)', 'success')
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: { tagCode, message: smsMessage.trim() },
      })

      if (error || !data?.ok) {
        showToast(data?.error || 'Failed to send SMS. Try again.', 'error')
      } else {
        recordContact(tagCode)
        setSmsModal(false)
        showToast('✉️ SMS delivered to vehicle owner', 'success')
      }
    } catch (err) {
      showToast('Network error. Please try again.', 'error')
    } finally {
      setSmsSending(false)
    }
  }

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-accent" />
          <p className="text-sm text-brand-muted">Loading tag…</p>
        </div>
      </div>
    )
  }

  // ── Error state ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-5">
        <div className="max-w-sm w-full text-center space-y-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
            style={{ background: 'rgba(239,68,68,0.12)' }}
          >
            <AlertCircle size={32} className="text-red-400" />
          </div>
          <h2 className="font-syne font-bold text-xl text-brand-text">Tag Unavailable</h2>
          <p className="text-sm text-brand-muted leading-relaxed">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 mx-auto text-sm text-brand-muted hover:text-accent transition-colors"
          >
            <ChevronLeft size={16} />
            About ParkPing
          </button>
        </div>
      </div>
    )
  }

  const { vehicle } = tagData
  const hasAnyChannel = vehicle.channel_call || vehicle.channel_whatsapp || vehicle.channel_sms
  const activeReason = customReason.trim() || selectedReason
  const reasonReady = activeReason.length > 0
  const rateLimited = rlStatus && !rlStatus.allowed
  const canContact  = reasonReady && !rateLimited

  return (
    <div className="min-h-screen bg-bg text-brand-text font-sans">
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* WhatsApp Modal */}
      {whatsappModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setWhatsappModal(false) }}
        >
          <div
            className="w-full max-w-[480px] rounded-t-3xl p-6 space-y-4 animate-slide-up"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-syne font-bold text-lg">Send WhatsApp to Owner</h3>
                <p className="text-xs text-brand-muted mt-0.5">Your message will be delivered privately via ParkPing</p>
              </div>
              <button onClick={() => setWhatsappModal(false)} className="p-2 rounded-xl text-brand-muted hover:text-brand-text transition-colors" style={{ background: '#1c1c26' }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs" style={{ background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)', color: '#25d366' }}>
              <Shield size={12} />
              Neither your number nor the owner's is shared
            </div>

            <form onSubmit={handleSendWhatsApp} className="space-y-3">
              <textarea
                ref={whatsappRef}
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value.slice(0, 500))}
                placeholder={`Hi, your ${tagData?.vehicle?.plate || 'vehicle'} is blocking my driveway. Please move it when you can.`}
                rows={4}
                className="w-full px-4 py-3 rounded-2xl text-sm text-brand-text placeholder-brand-muted outline-none resize-none transition-all"
                style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)' }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(37,211,102,0.5)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
              />
              <p className="text-right text-xs text-brand-muted">{whatsappMessage.length}/500</p>
              <button
                type="submit"
                disabled={whatsappSending || !whatsappMessage.trim()}
                className="w-full py-4 rounded-2xl font-syne font-bold text-base flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                style={{ background: '#25d366', color: '#fff', boxShadow: '0 0 20px rgba(37,211,102,0.3)' }}
              >
                {whatsappSending ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>💬 Send WhatsApp</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Call Modal */}
      {callModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setCallModal(false) }}
        >
          <div
            className="w-full max-w-[480px] rounded-t-3xl p-6 space-y-4 animate-slide-up"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-syne font-bold text-lg">Masked Call</h3>
                <p className="text-xs text-brand-muted mt-0.5">
                  We'll call you first, then connect you to the owner
                </p>
              </div>
              <button
                onClick={() => setCallModal(false)}
                className="p-2 rounded-xl text-brand-muted hover:text-brand-text transition-colors"
                style={{ background: '#1c1c26' }}
              >
                <X size={18} />
              </button>
            </div>

            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', color: '#00e5a0' }}
            >
              <Shield size={12} />
              Your number is never shared with the owner
            </div>

            <form onSubmit={handleInitiateCall} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">Your Mobile Number</label>
                <div className="flex rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  <span className="flex items-center px-3 text-sm font-semibold text-accent bg-surface2 shrink-0">+91</span>
                  <input
                    ref={callInputRef}
                    type="tel"
                    value={callerPhone}
                    onChange={(e) => setCallerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="98765 43210"
                    className="flex-1 px-3 py-3 bg-surface text-brand-text placeholder-brand-muted outline-none"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={callInitiating || callerPhone.length < 10}
                className="w-full py-4 rounded-2xl font-syne font-bold text-base text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.3)' }}
              >
                {callInitiating ? (
                  <><Loader2 size={18} className="animate-spin" /> Calling…</>
                ) : (
                  <>📞 Call Me Now</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* SMS Modal */}
      {smsModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setSmsModal(false) }}
        >
          <div
            className="w-full max-w-[480px] rounded-t-3xl p-6 space-y-4 animate-slide-up"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-syne font-bold text-lg">Send SMS to Owner</h3>
                <p className="text-xs text-brand-muted mt-0.5">
                  Your message will be delivered privately via ParkPing
                </p>
              </div>
              <button
                onClick={() => setSmsModal(false)}
                className="p-2 rounded-xl text-brand-muted hover:text-brand-text transition-colors"
                style={{ background: '#1c1c26' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Privacy badge */}
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', color: '#00e5a0' }}
            >
              <Shield size={12} />
              Neither your number nor the owner's is shared
            </div>

            {/* Message form */}
            <form onSubmit={handleSendSms} className="space-y-3">
              <textarea
                ref={textareaRef}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value.slice(0, 160))}
                placeholder={`Hi, your ${tagData?.vehicle?.plate || 'vehicle'} is blocking my driveway. Please move it when you can.`}
                rows={4}
                className="w-full px-4 py-3 rounded-2xl text-sm text-brand-text placeholder-brand-muted outline-none resize-none transition-all"
                style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)' }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(255,107,53,0.5)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
              />
              <p className="text-right text-xs text-brand-muted">{smsMessage.length}/160</p>

              <button
                type="submit"
                disabled={smsSending || !smsMessage.trim()}
                className="w-full py-4 rounded-2xl font-syne font-bold text-base text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                style={{ background: '#ff6b35', boxShadow: '0 0 20px rgba(255,107,53,0.3)' }}
              >
                {smsSending ? (
                  <><Loader2 size={18} className="animate-spin" /> Sending…</>
                ) : (
                  <><Send size={16} /> Send SMS</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}


      <div className="mx-auto max-w-[480px] px-5 py-8 space-y-6 animate-fade-in">
        {/* ParkPing branding (small) */}
        <div className="flex justify-center">
          <span className="font-syne font-extrabold text-base text-brand-muted">
            Park<span className="text-accent">Ping</span>
          </span>
        </div>

        {/* Vehicle / Pet card */}
        <div
          className="rounded-2xl p-5 text-center space-y-3"
          style={{
            background: '#13131a',
            border: '1px solid rgba(0,229,160,0.2)',
            boxShadow: '0 0 32px rgba(0,229,160,0.08)',
          }}
        >
          {/* Icon / Photo */}
          {vehicle.photo_url ? (
            <img
              src={vehicle.photo_url}
              alt={vehicle.pet_name || 'photo'}
              className="w-24 h-24 rounded-full object-cover mx-auto"
              style={{ border: '3px solid rgba(0,229,160,0.4)' }}
            />
          ) : (
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto text-4xl"
              style={{ background: '#1c1c26' }}
            >
              {vehicle.type === 'pet' ? (PET_ANIMAL_ICONS[vehicle.pet_animal] || '🐾') : VEHICLE_ICONS[vehicle.type] || '🚗'}
            </div>
          )}

          {/* Name / Plate */}
          <div>
            <p className="font-syne font-extrabold text-3xl tracking-widest text-brand-text" style={{ letterSpacing: '0.15em' }}>
              {(vehicle.type === 'pet' || vehicle.type === 'child') ? vehicle.pet_name : vehicle.plate}
            </p>
            <p className="text-sm text-brand-muted mt-0.5">
              {vehicle.type === 'pet'
                ? `${vehicle.pet_animal ? vehicle.pet_animal.charAt(0).toUpperCase() + vehicle.pet_animal.slice(1) : 'Pet'}${vehicle.pet_breed ? ` · ${vehicle.pet_breed}` : ''}`
                : vehicle.type === 'child'
                ? `Child${vehicle.pet_breed ? ` · ${vehicle.pet_breed}` : ''}`
                : VEHICLE_LABELS[vehicle.type]}
            </p>
          </div>

          {/* Privacy badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.25)' }}
          >
            <Shield size={13} className="text-accent" />
            <span className="text-xs font-semibold text-accent">Owner's number is private</span>
          </div>
        </div>

        {/* Parking location — vehicles only */}
        {parkingSession && vehicle.type !== 'pet' && vehicle.type !== 'child' && (
          <ScannerMap session={parkingSession} plate={vehicle.plate} />
        )}

        {/* Heading */}
        <div className="text-center">
          <h1 className="font-syne font-bold text-2xl text-brand-text mb-2">
            {vehicle.type === 'pet' ? '🐾 Found a lost pet?'
              : vehicle.type === 'child' ? '👶 Found a lost child?'
              : "What's the issue?"}
          </h1>
          <p className="text-sm text-brand-muted leading-relaxed">
            {vehicle.type === 'pet'
              ? 'Let the owner know. Your identity stays private.'
              : vehicle.type === 'child'
              ? 'Let the parent / guardian know. Your identity stays private.'
              : 'Select a reason to contact the owner. Your identity stays private.'}
          </p>
        </div>

        {/* Reason selector */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(vehicle.type === 'pet' ? PET_QUICK_REASONS : vehicle.type === 'child' ? CHILD_QUICK_REASONS : QUICK_REASONS).map((r) => (
              <button
                key={r.label}
                type="button"
                onClick={() => { setSelectedReason(r.label); setCustomReason('') }}
                className="flex items-center gap-2 px-3 py-3 rounded-2xl text-sm font-semibold text-left transition-all hover:scale-[1.02] active:scale-95"
                style={{
                  background: selectedReason === r.label && !customReason ? 'rgba(0,229,160,0.12)' : '#13131a',
                  border: `1px solid ${selectedReason === r.label && !customReason ? 'rgba(0,229,160,0.5)' : 'rgba(255,255,255,0.07)'}`,
                  color: selectedReason === r.label && !customReason ? '#00e5a0' : '#b0afc0',
                }}
              >
                <span>{r.icon}</span>
                <span className="leading-tight">{r.label}</span>
              </button>
            ))}
          </div>

          <div className="relative">
            <textarea
              value={customReason}
              onChange={(e) => { setCustomReason(e.target.value.slice(0, 200)); setSelectedReason('') }}
              placeholder="Or describe the issue in your own words…"
              rows={2}
              className="w-full px-4 py-3 rounded-2xl text-sm text-brand-text placeholder-brand-muted outline-none resize-none transition-all"
              style={{
                background: '#13131a',
                border: `1px solid ${customReason ? 'rgba(0,229,160,0.4)' : 'rgba(255,255,255,0.08)'}`,
              }}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(0,229,160,0.4)'; setSelectedReason('') }}
              onBlur={(e) => { if (!customReason) e.target.style.borderColor = 'rgba(255,255,255,0.08)' }}
            />
            {customReason && <p className="text-right text-xs text-brand-muted mt-1">{customReason.length}/200</p>}
          </div>
        </div>

        {/* Contact buttons — only shown after reason selected */}
        {hasAnyChannel ? (
          <div className="space-y-3">
            <p className="text-xs text-center text-brand-muted">
              {!reasonReady
                ? 'Select a reason above to contact the owner'
                : rateLimited
                  ? ''
                  : `Choose how to reach the owner · ${rlStatus?.contactsLeft ?? MAX_CONTACTS_PER_DAY} contact${(rlStatus?.contactsLeft ?? MAX_CONTACTS_PER_DAY) !== 1 ? 's' : ''} remaining today`
              }
            </p>

            {/* Rate limit notice */}
            {rateLimited && (
              <div
                className="rounded-2xl px-4 py-3 text-center text-sm space-y-0.5"
                style={{ background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.22)' }}
              >
                {rlStatus.reason === 'cooldown' ? (
                  <>
                    <p className="text-brand-muted font-semibold">⏱ Cooldown active</p>
                    <p className="text-brand-muted text-xs">
                      You can contact again in{' '}
                      <span className="font-bold" style={{ color: '#ff6b35' }}>{fmtCooldown(cooldownSecs)}</span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-brand-muted font-semibold">🚫 Daily limit reached</p>
                    <p className="text-brand-muted text-xs">Max 3 contacts per tag per 24 hours. Try again tomorrow.</p>
                  </>
                )}
              </div>
            )}

            {vehicle.channel_call && (
              <button
                onClick={handleCallClick}
                disabled={!canContact}
                className="w-full py-4 rounded-2xl font-syne font-bold text-lg text-bg flex items-center justify-center gap-3 transition-all hover:opacity-90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: '#00e5a0', boxShadow: canContact ? '0 0 24px rgba(0,229,160,0.35)' : 'none' }}
              >
                📞 Call Owner (Masked)
              </button>
            )}

            {vehicle.channel_whatsapp && (
              <button
                onClick={handleWhatsAppClick}
                disabled={!canContact}
                className="w-full py-4 rounded-2xl font-syne font-bold text-lg flex items-center justify-center gap-3 transition-all hover:opacity-90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: 'rgba(37,211,102,0.12)',
                  border: '1px solid rgba(37,211,102,0.35)',
                  color: '#25d366',
                }}
              >
                💬 WhatsApp Message
              </button>
            )}

            {vehicle.channel_sms && (
              <button
                onClick={openSmsModal}
                disabled={!canContact}
                className="w-full py-4 rounded-2xl font-syne font-bold text-lg flex items-center justify-center gap-3 transition-all hover:opacity-90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: 'rgba(255,107,53,0.12)',
                  border: '1px solid rgba(255,107,53,0.3)',
                  color: '#ff6b35',
                }}
              >
                ✉️ Send SMS
              </button>
            )}
          </div>
        ) : (
          <div
            className="rounded-2xl p-5 text-center"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p className="text-brand-muted text-sm">
              The owner has temporarily disabled all contact channels.
            </p>
          </div>
        )}

        {/* Privacy disclaimer */}
        <div
          className="rounded-2xl p-4 space-y-1.5"
          style={{ background: 'rgba(0,229,160,0.05)', border: '1px solid rgba(0,229,160,0.12)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield size={13} className="text-accent" />
            <p className="text-xs font-semibold text-accent">Privacy Guarantee</p>
          </div>
          <p className="text-xs text-brand-muted leading-relaxed">
            Neither you nor the vehicle owner's real phone numbers are ever shared. All communication is routed through ParkPing's secure proxy layer. This contact attempt is logged anonymously.
          </p>
        </div>

        {/* Powered by */}
        <div className="text-center pb-4">
          <p className="text-xs text-brand-muted">
            Powered by{' '}
            <span
              className="font-syne font-bold cursor-pointer hover:text-accent transition-colors"
              onClick={() => navigate('/')}
            >
              Park<span className="text-accent">Ping</span>
            </span>
            {' '}· Privacy-first parking contacts
          </p>
        </div>
      </div>
    </div>
  )
}
